import { Minus, Plus } from 'lucide-react';

import { Button } from './Button';
import { cn } from './utils';


/**
 * Bounded-integer − / value / + control, extracted from the two screens that
 * hand-rolled it: `components/MarkSoldModal/StepRecord.jsx` (quantity sold) and
 * `pages/Intake/VisualPicker.jsx` (`TrayRow` quantity).
 *
 * Shape follows the .pen stepper atom (kit PIIo9/ItotJ · "Quantity sold F"
 * vH8gj/JvWde · intake tray row y1ugou): a hairline-strong / md-radius control
 * frame — the same chrome `fieldFrame` gives inputs, per the DESIGN.md
 * `stepper` token — with the icon buttons INSIDE it and the value centred
 * between them.
 *
 * `md` is the 40px form-field size (DESIGN.md `stepper.height`, surface-1 fill,
 * surface-2 icon buttons); `sm` is the compact in-row size the intake tray
 * uses, whose frame and icon buttons carry no fill (per the .pen tray row).
 *
 * Controlled: `onChange` receives the clamped next value. `label` names the
 * group and both buttons ("Decrease {label}" / "Increase {label}").
 */
const stepperSizes = {
  md: {
    root: 'h-10 bg-surface-1 p-1',
    button: 'size-8 rounded-sm bg-surface-2 text-ink hover:bg-surface-3 hover:text-ink',
    icon: 'size-4',
    value: 'flex-1 px-1 text-center text-sm font-semibold text-ink',
  },
  sm: {
    root: 'h-8 px-0.5',
    button: 'size-7 rounded-sm text-ink-muted',
    icon: 'size-3.5!',
    value: 'min-w-6 flex-1 px-0.5 text-center text-table font-semibold text-ink',
  },
};

/**
 * DESIGN.md `stepper` also asks for "− / + buttons ≥44px on touch", but the
 * frames draw them at 32px (`yaShR`) / 28px (mobile tray `k1wQj`). Widening the
 * TARGET on coarse pointers satisfies both: the drawn control is unchanged and a
 * finger gets 44px. The two buttons sit at opposite ends of the control with the
 * value between them, so the two 44px overlays never meet (sm: centres ~52px
 * apart on a ~84px control).
 */
const TOUCH_TARGET = 'touch-target-44';


function NumberStepper(props) {
  const {
    value, onChange, label, format, min = 1, max, size = 'md', className,
  } = props;
  const styles = stepperSizes[size];
  const canDecrement = value > min;
  const canIncrement = max == null || value < max;

  return (
    <div
      aria-label={label}
      className={cn(
        'flex items-center rounded-md border border-hairline-strong',
        styles.root,
        className,
      )}
      role="group"
    >
      <Button
        size="icon"
        variant="ghost"
        disabled={!canDecrement}
        aria-label={`Decrease ${label}`}
        className={cn(styles.button, TOUCH_TARGET)}
        onClick={() => onChange(Math.max(min, value - 1))}
      >
        <Minus className={styles.icon} />
      </Button>

      <span className={styles.value}>{format ? format(value) : value}</span>

      <Button
        size="icon"
        variant="ghost"
        disabled={!canIncrement}
        aria-label={`Increase ${label}`}
        className={cn(styles.button, TOUCH_TARGET)}
        onClick={() => onChange(max == null ? value + 1 : Math.min(max, value + 1))}
      >
        <Plus className={styles.icon} />
      </Button>
    </div>
  );
}

NumberStepper.displayName = 'NumberStepper';
export { NumberStepper };
export default NumberStepper;
